{/* Copyright 2026 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {InstallCommand} from '../../../src/InstallCommand';

import {Layout} from '../../../src/Layout';
export default Layout;

import docs from 'docs:@react-spectrum/s2';

export const hideNav = true;
export const section = 'Releases';
export const tags = ['release', 'React Aria'];
export const date = 'October 8, 2026';
export const title = 'v1.22.0';
export const description = 'This release adds a new Sheet component, Turbopack support for locale optimization, and improved handling of interactive widgets in overlay placement, along with bug fixes.';
export const isSubpage = true;

# v1.22.0

This release introduces [Sheet](../Sheet), a swipeable overlay that slides in from the edge of the viewport, built on native browser scroll gestures. This component is perfect for implementing touch-friendly trays such as mobile sheets, navigation drawers, and notification banners. 

```tsx render hideCode
  "use client";
  import {useState} from 'react';
  import {SheetTrigger} from 'react-aria-components/Sheet';
  import {Sheet, Heading} from 'vanilla-starter/Sheet';
  import {Button} from 'vanilla-starter/Button';
  import {Minus, Plus, ShoppingCart} from 'lucide-react';

  function Example(props) {
    let [items, setItems] = useState([
      {name: 'Pork Bao', detail: 'Steamed · 2 pieces', emoji: '🥟', price: 4.5, qty: 2},
      {name: 'Spicy Ramen', detail: 'Extra spicy · Soft egg', emoji: '🍜', price: 13, qty: 1},
      {name: 'Mango Boba', detail: 'Less ice · 50% sweet', emoji: '🧋', price: 5.75, qty: 1}
    ]);
    let update = (name, delta) => setItems(items => items
      .map(item => item.name === name ? {...item, qty: item.qty + delta} : item)
      .filter(item => item.qty > 0));
    let subtotal = items.reduce((sum, item) => sum + item.price * item.qty, 0);
    let delivery = items.length ? 2.99 : 0;
    
    return (
      <SheetTrigger>
        <Button>
          <ShoppingCart size={18} />
          <span>Cart ({items.reduce((n, item) => n + item.qty, 0)})</span>
        </Button>
        {/*- begin highlight -*/}
        <Sheet {...props}/* PROPS */>
        {/*- end highlight -*/}
          {/*- begin collapse -*/}
          <div style={{display: 'flex', flexDirection: 'column', minHeight: '100%'}}>
            <Heading slot="title">Your order</Heading>
            <p style={{margin: '0 0 20px', color: 'var(--gray-1000)'}}>Delivering from Bao Bros · 25–35 min</p>
            <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', flexGrow: 1}}>
              {items.map(item => (
                <div key={item.name} style={{display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)'}}>
                  <span style={{width: 'calc(15 * var(--spacing))', height: 'calc(15 * var(--spacing))', borderRadius: 12, background: 'var(--gray-200)', fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>{item.emoji}</span>
                  <div style={{display: 'flex', alignItems: 'center', columnGap: 'var(--spacing-3)', rowGap: 4, flexWrap: 'wrap', flexGrow: 1}}>
                    <div style={{flexGrow: 100, minWidth: 0}}>
                      <div style={{fontWeight: 600}}>{item.name}</div>
                      <div style={{fontSize: 12, color: 'var(--gray-1000)'}}>{item.detail}</div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 4, flexGrow: 1}}>
                      <Button aria-label={`Remove one ${item.name}`} variant="secondary" onPress={() => update(item.name, -1)}><Minus size={16} /></Button>
                      <span style={{minWidth: 20, textAlign: 'center'}}>{item.qty}</span>
                      <Button aria-label={`Add one ${item.name}`} variant="secondary" onPress={() => update(item.name, 1)}><Plus size={16} /></Button>
                      <span style={{minWidth: '6ch', textAlign: 'end', flexGrow: 1}}>${(item.price * item.qty).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{display: 'flex', flexDirection: 'column', gap: 4, margin: '20px 0', paddingTop: 16, borderTop: '1px solid var(--gray-300)'}}>
              <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div style={{display: 'flex', justifyContent: 'space-between'}}><span>Delivery fee</span><span>${delivery.toFixed(2)}</span></div>
              <div style={{display: 'flex', justifyContent: 'space-between', fontWeight: 600}}><span>Total</span><span>${(subtotal + delivery).toFixed(2)}</span></div>
            </div>
            <Button slot="close" style={{width: '100%'}}>Place order</Button>
          </div>
          {/*- end collapse -*/}
        </Sheet>
      </SheetTrigger>
    );
  }
```
It also adds Turbopack support to `@react-aria/optimize-locales-plugin`, overlay placement that accounts for the virtual keyboard, and much more!

As always, many thanks to all of our contributors!

## Changelog

### General Changes
- Add `suppressHydrationWarning` to `GlobalDOMAttributes` so it can be forwarded by `filterDOMProps` - [@miszuto](https://github.com/miszuto) - [PR](https://github.com/adobe/react-spectrum/pull/10527)
- Fix `TypeError` in `preventFocus` when the window is focused or blurred during a press - [@microbit-matt-hillsdon](https://github.com/microbit-matt-hillsdon) - [PR](https://github.com/adobe/react-spectrum/pull/10592)
- Skip visibility check in `Focusable` and `Pressable` during development so children inside hidden subtrees no longer warn - [@SimenB](https://github.com/SimenB) - [PR](https://github.com/adobe/react-spectrum/pull/10547)
- Throw an error on invalid `rgb()` and `rgba()` color strings - [@giaBaoJS](https://github.com/giaBaoJS) - [PR](https://github.com/adobe/react-spectrum/pull/10580)
- Enable the `useControlledState` early effect in React Native - [@su-jin1425](https://github.com/su-jin1425) - [PR](https://github.com/adobe/react-spectrum/pull/10550)
- Add support for interactive widgets, such as the virtual keyboard, when positioning overlays - [@nwidynski](https://github.com/nwidynski) - [PR](https://github.com/adobe/react-spectrum/pull/10102)
- Add Turbopack support to `@react-aria/optimize-locales-plugin` - [@stefanprobst](https://github.com/stefanprobst) - [PR](https://github.com/adobe/react-spectrum/pull/10462)
### Collections
- Warn about duplicate collection keys during development - [@greglum](https://github.com/greglum) - [PR](https://github.com/adobe/react-spectrum/pull/10605)
### ComboBox
- Re-open popover when async items arrive after an empty response - [@costajohnt](https://github.com/costajohnt) - [PR](https://github.com/adobe/react-spectrum/pull/9823)
### Date/Time
- Include the final constrained year in the year picker - [@ZedingZhang](https://github.com/ZedingZhang) - [PR](https://github.com/adobe/react-spectrum/pull/10536)
- Use default era when localized value is not supported in CLDR - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/10698)
### Docs
- Fix typos - [@olitreadwell](https://github.com/olitreadwell) - [PR](https://github.com/adobe/react-spectrum/pull/10578)
### Forms
- Add development warnings that detect invalid focus movement in forms - [@snowystinger](https://github.com/snowystinger) - [PR](https://github.com/adobe/react-spectrum/pull/10492)
### Link
- Add `aria-current` support to `LinkContext` - [@andrew-pledge-io](https://github.com/andrew-pledge-io) - [PR](https://github.com/adobe/react-spectrum/pull/10573)
### Popover
- Clear Popover context inside Popover - [@reidbarber](https://github.com/reidbarber) - [PR](https://github.com/adobe/react-spectrum/pull/10685)
### Sheet
- Add Sheet component - [@devongovett](https://github.com/devongovett) - [PR](https://github.com/adobe/react-spectrum/pull/10674)
### Table
- Fix `useGridCell` overriding focus restoration for focusable children, such as a dialog trigger inside a cell - [@jsmitrah](https://github.com/jsmitrah) - [PR](https://github.com/adobe/react-spectrum/pull/10228)
### Tailwind
- Add note to the Tailwind plugin documentation about how data attributes map to Tailwind selectors - [@travisrcory](https://github.com/travisrcory) - [PR](https://github.com/adobe/react-spectrum/pull/10574)
### TokenField
- Export `TokenFieldContext` as a value rather than a type - [@LFDanLu](https://github.com/LFDanLu) - [PR](https://github.com/adobe/react-spectrum/pull/10612)
- Fix cursor from jumping after editing tokens - [@minwookshin](https://github.com/minwookshin) - [PR](https://github.com/adobe/react-spectrum/pull/10676)
### Tree
- Fix `useTreeData` subtree move validation - [@su-jin1425](https://github.com/su-jin1425) - [PR](https://github.com/adobe/react-spectrum/pull/10560)

## Released packages

```
 - @internationalized/number@3.6.9
 - @react-types/shared@3.37.0
 - @react-aria/optimize-locales-plugin@2.1.0
 - react-aria@3.53.0
 - react-aria-components@1.22.0
 - react-stately@3.51.0
```
